import { InventoryService } from '../services/inventoryService';
import {
  STEP1_FIRST_STEP_INVENTORY_TYPE,
  STEP1_FIRST_STEP_VERSION,
  Step1WizardAnswerEntry,
  Step1WritingInventoryPayload,
} from '../types/step1Writing';
import { estimateReadTime } from './readTimeEstimate';

export const STEP1_ARCHIVE_VERSION_PREFIX = 'archive-';
export const STEP1_MAX_SERVER_ARCHIVES = 50;
/** Minimum time between automatic dated snapshots during normal editing */
export const STEP1_ARCHIVE_MIN_INTERVAL_MS = 5 * 60 * 1000;

const PAYLOAD_TYPE = 'step1_writing_payload';

let lastAutosaveArchiveAtMs = 0;

export function wizardAnswersHaveContent(
  answers: Record<string, Step1WizardAnswerEntry> | undefined
): boolean {
  return Object.values(answers || {}).some((e) => e.examples?.some((x) => x.trim().length > 0));
}

function emptyPayload(): Step1WritingInventoryPayload {
  return {
    _type: PAYLOAD_TYPE,
    wizardAnswers: {},
    wizardStepIndex: 0,
    wizardChapterId: 'ch1',
    lastSavedAt: new Date().toISOString(),
  };
}

export function payloadHasMeaningfulStep1Content(payload: Step1WritingInventoryPayload): boolean {
  return (
    wizardAnswersHaveContent(payload.wizardAnswers) ||
    (payload.contentText || '').trim().length > 20
  );
}

/** Stable digest for detecting meaningful changes between saves */
export function step1PayloadDigest(payload: Step1WritingInventoryPayload): string {
  const { lastSavedAt: _ls, archivedAt: _aa, archiveReason: _ar, ...copy } = payload;
  return JSON.stringify(copy);
}

export function createArchiveVersionId(at = new Date()): string {
  return `${STEP1_ARCHIVE_VERSION_PREFIX}${at.toISOString().replace(/[:.]/g, '-')}`;
}

export function formatArchiveVersionLabel(version: string, payload?: Step1WritingInventoryPayload): string {
  const fromPayload = payload?.archivedAt;
  if (fromPayload) {
    const d = new Date(fromPayload);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }
  const encoded = version.slice(STEP1_ARCHIVE_VERSION_PREFIX.length);
  const iso = encoded.replace(/-(\d{3})Z$/, '.$1Z').replace(/-/g, (m, offset, s) => {
    const idx = s.indexOf('T');
    if (idx < 0) return m;
    return offset > idx ? ':' : m;
  });
  const d = new Date(iso);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return version;
}

export async function loadStep1PayloadFromVersion(
  version: string
): Promise<Step1WritingInventoryPayload | null> {
  const items = await InventoryService.getInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, version);
  const payload = items.find((i) => i._type === PAYLOAD_TYPE) as Step1WritingInventoryPayload | undefined;
  return payload ? { ...emptyPayload(), ...payload, _type: PAYLOAD_TYPE } : null;
}

export async function saveStep1PayloadToVersion(
  version: string,
  payload: Step1WritingInventoryPayload,
  archiveMeta?: { archiveReason?: string }
): Promise<void> {
  const toSave: Step1WritingInventoryPayload = {
    ...payload,
    _type: PAYLOAD_TYPE,
    lastSavedAt: new Date().toISOString(),
    archivedAt: new Date().toISOString(),
    archiveReason: archiveMeta?.archiveReason,
  };
  await InventoryService.saveInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, version, [toSave]);
}

export async function listStep1ArchiveVersions(): Promise<string[]> {
  const all = await InventoryService.listInventoryVersions(STEP1_FIRST_STEP_INVENTORY_TYPE);
  return all
    .filter((v) => v.startsWith(STEP1_ARCHIVE_VERSION_PREFIX) && v !== STEP1_FIRST_STEP_VERSION)
    .sort()
    .reverse();
}

export async function deleteAllStep1Archives(): Promise<void> {
  const versions = await listStep1ArchiveVersions();
  for (const version of versions) {
    try {
      await InventoryService.deleteInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, version);
    } catch {
      // Best-effort — continue clearing remaining copies
    }
  }
}

export async function pruneStep1Archives(): Promise<void> {
  const archives = await listStep1ArchiveVersions();
  if (archives.length <= STEP1_MAX_SERVER_ARCHIVES) return;
  const toDelete = archives.slice(STEP1_MAX_SERVER_ARCHIVES);
  for (const version of toDelete) {
    try {
      await InventoryService.deleteInventory(STEP1_FIRST_STEP_INVENTORY_TYPE, version);
    } catch {
      // Best-effort retention cleanup
    }
  }
}

export function shouldThrottleAutosaveArchive(): boolean {
  return Date.now() - lastAutosaveArchiveAtMs < STEP1_ARCHIVE_MIN_INTERVAL_MS;
}

export function markAutosaveArchiveCreated(): void {
  lastAutosaveArchiveAtMs = Date.now();
}

/** Write a dated server snapshot of the current payload (full copy). */
export async function archiveStep1Payload(
  payload: Step1WritingInventoryPayload,
  archiveReason: string
): Promise<string | null> {
  if (!payloadHasMeaningfulStep1Content(payload)) return null;
  const version = createArchiveVersionId();
  await saveStep1PayloadToVersion(version, payload, { archiveReason });
  await pruneStep1Archives();
  return version;
}

export interface Step1ArchiveSummary {
  version: string;
  label: string;
  wordCount: number;
  archiveReason?: string;
}

export async function listStep1ArchiveSummaries(): Promise<Step1ArchiveSummary[]> {
  const versions = await listStep1ArchiveVersions();
  const summaries: Step1ArchiveSummary[] = [];
  for (const version of versions.slice(0, 20)) {
    try {
      const payload = await loadStep1PayloadFromVersion(version);
      if (!payload) continue;
      summaries.push({
        version,
        label: formatArchiveVersionLabel(version, payload),
        wordCount: estimateReadTime(payload.contentText || '').wordCount,
        archiveReason: payload.archiveReason,
      });
    } catch {
      // Skip unreadable archive entries
    }
  }
  return summaries;
}
